import { LitElement, html, nothing } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { wsClient } from '../../services/websocket-client.js';
import type { TerminalCreatedMessage, TerminalOutputMessage, TerminalErrorMessage } from '../../types/project.js';

// Simple ANSI escape sequence stripper
function stripAnsi(text: string): string {
  return text.replace(/\x1b\[[0-9;]*[a-zA-Z]/g, '');
}

@customElement('cp-terminal-panel')
export class CpTerminalPanel extends LitElement {
  createRenderRoot() { return this; }

  @property({ type: Number }) projectId = 0;

  @state() private _sessionId = '';
  @state() private _output = '';
  @state() private _connected = false;
  @state() private _connecting = false;
  @state() private _error = '';

  private _wsHandler = this._onWsMessage.bind(this);

  connectedCallback(): void {
    super.connectedCallback();
    wsClient.addEventListener('ws-message', this._wsHandler as EventListener);
  }

  disconnectedCallback(): void {
    super.disconnectedCallback();
    wsClient.removeEventListener('ws-message', this._wsHandler as EventListener);
    this._closeSession();
  }

  updated(changed: Map<string, unknown>): void {
    if (changed.has('projectId')) {
      // Close existing session when project changes
      this._closeSession();
    }
  }

  private _onWsMessage(e: CustomEvent): void {
    const msg = e.detail as { type: string; payload: unknown };

    switch (msg.type) {
      case 'terminal:created': {
        const payload = msg.payload as TerminalCreatedMessage;
        this._sessionId = payload.sessionId;
        this._connected = true;
        this._connecting = false;
        this._error = '';
        this._output = `Connected to ${payload.projectPath}\r\n`;
        break;
      }

      case 'terminal:output': {
        const payload = msg.payload as TerminalOutputMessage;
        if (payload.sessionId === this._sessionId) {
          this._output += stripAnsi(payload.data);
          this.requestUpdate();
          // Auto-scroll after render
          this.updateComplete.then(() => this._scrollToBottom());
        }
        break;
      }

      case 'terminal:error': {
        const payload = msg.payload as TerminalErrorMessage;
        this._error = payload.error;
        this._connecting = false;
        break;
      }

      case 'terminal:exited': {
        const payload = msg.payload as { sessionId: string };
        if (payload.sessionId === this._sessionId) {
          this._output += '\r\n[Session ended]\r\n';
          this._connected = false;
          this._sessionId = '';
        }
        break;
      }

      case 'terminal:closed': {
        const payload = msg.payload as { sessionId: string };
        if (payload.sessionId === this._sessionId) {
          this._connected = false;
          this._sessionId = '';
        }
        break;
      }
    }
  }

  private _openSession(): void {
    if (!this.projectId || this._connecting) return;
    this._connecting = true;
    this._error = '';
    this._output = '';

    wsClient.send({
      type: 'terminal:create',
      payload: { projectId: this.projectId },
      timestamp: new Date().toISOString(),
    });
  }

  private _closeSession(): void {
    if (this._sessionId) {
      wsClient.send({
        type: 'terminal:close',
        payload: { sessionId: this._sessionId },
        timestamp: new Date().toISOString(),
      });
      this._sessionId = '';
      this._connected = false;
    }
  }

  private _onInputKeydown(e: KeyboardEvent): void {
    if (e.key === 'Enter') {
      const input = e.target as HTMLInputElement;
      const command = input.value;
      if (!command && !this._sessionId) return;

      wsClient.send({
        type: 'terminal:input',
        payload: { sessionId: this._sessionId, data: command + '\n' },
        timestamp: new Date().toISOString(),
      });
      input.value = '';
    }
  }

  private _sendShortcut(command: string): void {
    if (!this._sessionId) return;
    wsClient.send({
      type: 'terminal:input',
      payload: { sessionId: this._sessionId, data: command + '\n' },
      timestamp: new Date().toISOString(),
    });
  }

  private _scrollToBottom(): void {
    const outputEl = this.querySelector('.cp-terminal-panel__output');
    if (outputEl) {
      outputEl.scrollTop = outputEl.scrollHeight;
    }
  }

  private _renderEmpty() {
    return html`
      <div class="cp-terminal-panel__empty">
        <span>Terminal nicht verbunden</span>
        <button
          class="cp-terminal-panel__connect-btn"
          ?disabled=${this._connecting || !this.projectId}
          @click=${this._openSession}
        >
          ${this._connecting ? 'Verbinde...' : 'Terminal starten'}
        </button>
        ${this._error ? html`
          <span style="color: var(--color-accent-error); font-size: var(--font-size-xs);">${this._error}</span>
        ` : nothing}
      </div>
    `;
  }

  render() {
    if (!this._connected && !this._connecting) {
      return html`
        <div class="cp-terminal-panel">
          ${this._renderEmpty()}
        </div>
      `;
    }

    return html`
      <div class="cp-terminal-panel">
        <div class="cp-terminal-panel__header">
          <span class="cp-terminal-panel__title">
            <span class="cp-terminal-panel__status-dot ${this._connected ? 'cp-terminal-panel__status-dot--connected' : 'cp-terminal-panel__status-dot--disconnected'}"></span>
            Terminal
          </span>
          <div class="cp-terminal-panel__actions">
            <button
              class="cp-terminal-panel__shortcut"
              ?disabled=${!this._connected}
              @click=${() => this._sendShortcut('/create-spec')}
            >
              Create Spec
            </button>
            <button
              class="cp-terminal-panel__shortcut"
              ?disabled=${!this._connected}
              @click=${() => this._sendShortcut('/execute-tasks')}
            >
              Execute Tasks
            </button>
          </div>
        </div>

        <div class="cp-terminal-panel__output">${this._output}</div>

        <div class="cp-terminal-panel__input-row">
          <span class="cp-terminal-panel__prompt">$</span>
          <input
            class="cp-terminal-panel__input"
            type="text"
            placeholder="Kommando eingeben..."
            ?disabled=${!this._connected}
            @keydown=${this._onInputKeydown}
          />
        </div>
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'cp-terminal-panel': CpTerminalPanel;
  }
}
